import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ExternalLink, Loader2, PencilRuler, Trash2 } from 'lucide-react';
import type { FloorplanZone, FloorplanZoneOutlineShape } from '../../types';

interface RetailerLocationOption {
  id: number;
  name: string;
}

interface RetailerMapEditPanelProps {
  zone: FloorplanZone | null;
  colour: string | null;
  retailerId?: number;
  locations: RetailerLocationOption[];
  unavailableLocationIds?: Set<number>;
  isDeletingLocation?: boolean;
  onUpdate: (patch: Partial<FloorplanZone>) => void;
  onLocationChange: (location: RetailerLocationOption) => void;
  onRedraw: () => void;
  onRemove: () => void;
  onDeleteLocation: () => void;
}

const OUTLINE_SHAPES: FloorplanZoneOutlineShape[] = ['rectangle', 'rounded', 'ellipse'];

export function RetailerMapEditPanel({
  zone,
  colour,
  retailerId,
  locations,
  unavailableLocationIds = new Set<number>(),
  isDeletingLocation = false,
  onUpdate,
  onLocationChange,
  onRedraw,
  onRemove,
  onDeleteLocation,
}: RetailerMapEditPanelProps) {
  if (!zone) {
    return (
      <aside className="h-full rounded-xl border bg-white p-4">
        <h2 className="font-semibold text-gray-900">Edit store map</h2>
        <p className="text-sm text-gray-600 mt-1">
          Select a coloured location area to move, resize, restyle, redraw or remove it.
        </p>
      </aside>
    );
  }

  const resolvedColour = zone.outlineColor ?? colour ?? '#0284c7';

  return (
    <aside className="h-full rounded-xl border bg-white overflow-hidden">
      <div className="px-4 py-3 border-b bg-blue-50">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Editing location</p>
        <h2 className="font-semibold text-gray-900 mt-0.5">{zone.label}</h2>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-sm text-gray-600">
          Drag the area to move it. Use its corner handles to resize it.
        </p>

        <div>
          <Label className="text-xs">Store location</Label>
          <Select
            value={zone.instoreLocationId != null ? String(zone.instoreLocationId) : undefined}
            onValueChange={(value) => {
              const location = locations.find((item) => item.id === Number(value));
              if (location) onLocationChange(location);
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Choose a store location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((location) => {
                const isMappedElsewhere = unavailableLocationIds.has(location.id);
                return (
                  <SelectItem
                    key={location.id}
                    value={String(location.id)}
                    disabled={isMappedElsewhere}
                  >
                    {location.name}{isMappedElsewhere ? ' — already mapped' : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-gray-500">
            Campaigns using this store location will map to this shape automatically.
          </p>
        </div>

        <div>
          <Label className="text-xs" htmlFor={`retailer-map-colour-${zone.id}`}>Layer colour</Label>
          <div className="flex h-10 items-center gap-2 rounded-md border bg-white px-2 mt-1">
            <input
              id={`retailer-map-colour-${zone.id}`}
              type="color"
              className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
              value={resolvedColour}
              onChange={(event) => onUpdate({ outlineColor: event.target.value })}
            />
            <span className="font-mono text-xs uppercase text-slate-500">{resolvedColour}</span>
          </div>
        </div>

        <div>
          <Label className="text-xs">Outline shape</Label>
          <Select
            value={zone.outlineShape ?? 'rounded'}
            onValueChange={(value: FloorplanZoneOutlineShape) => onUpdate({ outlineShape: value })}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OUTLINE_SHAPES.map((shape) => (
                <SelectItem key={shape} value={shape}>
                  {shape.charAt(0).toUpperCase() + shape.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Line weight</Label>
          <Select
            value={String(zone.outlineStrokeWidth ?? 0.2)}
            onValueChange={(value) => onUpdate({ outlineStrokeWidth: Number(value) })}
          >
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0.12">Fine</SelectItem>
              <SelectItem value="0.2">Standard</SelectItem>
              <SelectItem value="0.35">Bold</SelectItem>
              <SelectItem value="0.5">Highlight</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 border-t pt-4">
          <Button type="button" variant="outline" size="sm" className="w-full" onClick={onRedraw}>
            <PencilRuler className="h-4 w-4 mr-1" />
            Redraw area
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full text-red-700 hover:text-red-800"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove shape only
          </Button>

          {zone.instoreLocationId != null && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-700 hover:bg-red-50 hover:text-red-800"
                  disabled={isDeletingLocation}
                >
                  {isDeletingLocation
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Trash2 className="h-4 w-4 mr-1" />}
                  Delete location altogether
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete “{zone.label}” everywhere?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes the retailer-wide store location and removes its
                    floorplan shape. It cannot be deleted while campaign posting instructions
                    still reference it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep location</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={onDeleteLocation}
                  >
                    Delete location
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {retailerId != null && (
          <Button type="button" variant="link" size="sm" className="h-auto px-0 text-xs" asChild>
            <Link href={`/admin/retailers/${retailerId}/in-store-locations`}>
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Manage all retailer locations
            </Link>
          </Button>
        )}
      </div>
    </aside>
  );
}
